// client/src/pages/secure/ChangePasswordPage.js
// Self-service password change for a logged-in user. Doubles as the forced-change
// screen after an admin issues a temporary password (user.mustChangePassword).
import React from 'react';

const ChangePasswordPage = ({ user, forced = false }) => {
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 12) return setError('New password must be at least 12 characters.');
        if (newPassword !== confirm) return setError('New password and confirmation do not match.');

        setBusy(true);
        try {
            const resp = await fetch('/api/auth/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
                },
                body: JSON.stringify({ currentPassword, newPassword }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Could not change password.');
            setDone(true);
            // A fresh login clears the must_change_password flag from the token.
            setTimeout(() => {
                localStorage.removeItem('jwtToken');
                window.location.href = '/portal';
            }, 1800);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const card = 'w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg';
    const inputCls = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm';

    const body = (
        <div className={card}>
            <div>
                <h2 className="text-2xl font-bold text-gray-900">
                    {forced ? 'Set a new password' : 'Change your password'}
                </h2>
                {forced && (
                    <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        You're using a temporary password. Please choose your own password to continue.
                    </p>
                )}
            </div>

            {done ? (
                <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 text-sm">
                    Password updated. Redirecting you to sign in with your new password…
                </p>
            ) : (
                <form className="space-y-4" onSubmit={submit}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
                        <input type="password" autoComplete="current-password" required className={inputCls}
                            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                        <input type="password" autoComplete="new-password" required className={inputCls}
                            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        <p className="text-xs text-gray-500 mt-1">At least 12 characters.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                        <input type="password" autoComplete="new-password" required className={inputCls}
                            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <button type="submit" disabled={busy}
                        className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:bg-indigo-400">
                        {busy ? 'Saving…' : 'Update password'}
                    </button>
                </form>
            )}
        </div>
    );

    // When forced, take over the whole screen so the user can't skip it.
    if (forced) {
        return <div className="flex items-center justify-center min-h-screen bg-slate-100">{body}</div>;
    }
    return <div className="flex justify-center">{body}</div>;
};

export default ChangePasswordPage;
